/**
 * Storage Query Generator
 *
 * Generates GraphQL queries and mutations from StorageConfig metadata.
 * Used by UI code to dynamically fetch enum options and read/write config
 * values from various storage backends (JSONB, link tables, dedicated columns).
 *
 * @module storage/query-generator
 */

import type {
  EnumSourceConfig,
  SelectionSet,
  WhereClause,
  WhereOperator,
  OrderByClause,
  LinkTableLevelStorageConfig,
  DedicatedColumnLevelStorageConfig,
  AuditColumnConfig,
} from '../storage-types.js';
import type { ConfigLevel, Definition } from '../types.js';
import { getConfigLevelInfo } from '../types.js';

// =============================================================================
// Audit Column Support
// =============================================================================

/**
 * Package version for default audit identifier.
 * Used when no updatedBy is provided to identify automated/SDK changes.
 */
const PACKAGE_VERSION = '0.14.0';
const DEFAULT_UPDATED_BY = `sdk-configs@${PACKAGE_VERSION}`;

/**
 * Values to use for audit columns in mutations.
 */
export interface AuditValues {
  /**
   * Email or identifier of the user/actor making the change.
   * @example 'user@example.com' - From JWT in UI
   * @example 'sdk-configs@0.14.0' - Default for automated changes
   */
  updatedBy: string;
  /**
   * Timestamp for the change. Defaults to current time if not provided.
   */
  updatedAt?: Date | string;
}

/**
 * Resolves the updatedBy value, using default if not provided.
 */
export function resolveUpdatedBy(updatedBy?: string): string {
  return updatedBy ?? DEFAULT_UPDATED_BY;
}

/**
 * Formats a timestamp for GraphQL/Hasura.
 */
function formatTimestamp(date?: Date | string): string {
  if (!date) {
    return new Date().toISOString();
  }
  if (date instanceof Date) {
    return date.toISOString();
  }
  return date;
}

// =============================================================================
// Types
// =============================================================================

/**
 * A single mutation field within a GraphQL mutation.
 * Used for batching multiple mutations together.
 */
export interface MutationField {
  /** The field name (e.g., 'update_customers_by_pk') */
  fieldName: string;
  /** The complete field string including arguments and selection set */
  fieldBody: string;
  /** Variable names used by this field (with definition prefix) */
  variables: string[];
}

/**
 * Options for generating queries with batching support.
 */
export interface GeneratorOptions {
  /**
   * When true, generates unique variable names using definition key + level.
   * This allows multiple mutations to be combined without collisions.
   * @default false (for backward compatibility)
   */
  forBatching?: boolean;

  /**
   * Email or identifier of the user/actor making the change.
   * Used for createdby/updatedby audit columns when the storage config
   * defines them.
   *
   * If not provided, defaults to 'sdk-configs@{version}' for automated changes.
   *
   * @example 'user@example.com' // From JWT in UI
   */
  updatedBy?: string;
}

/**
 * Result of combining multiple mutations into a batch.
 */
export interface BatchedMutation {
  /** The complete batched GraphQL mutation string */
  query: string;
  /** Operation name for the batched mutation */
  operationName: string;
  /** All variable types merged from all mutations */
  variableTypes: Record<string, string>;
  /** Mapping from definition keys to their variable prefixes */
  variablePrefixes: Record<string, string>;
}

/**
 * Return type for generated queries/mutations.
 * Includes the query string, operation name, and variable type information.
 */
export interface GeneratedQuery {
  /** The GraphQL query/mutation string */
  query: string;
  /** The GraphQL operation name (e.g., "GetCertificationOptions") */
  operationName: string;
  /** Variable name to GraphQL type mapping (e.g., { regionId: 'bigint!' }) */
  variableTypes: Record<string, string>;
  /** Individual mutation fields for batching (only present for mutations with forBatching: true) */
  mutationFields?: MutationField[];
  /** The variable prefix used (definition key + level, only when forBatching: true) */
  variablePrefix?: string;
}

/**
 * Complete set of generated queries for a storage config.
 */
export interface GeneratedStorageQueries {
  /** Query for fetching enum options (if enum source is defined) */
  enumOptionsQuery?: GeneratedQuery;
  /** Getter queries keyed by config level */
  getterQueries: Partial<Record<ConfigLevel, GeneratedQuery>>;
  /** Setter mutations keyed by config level */
  setterMutations: Partial<Record<ConfigLevel, GeneratedQuery>>;
  /** Delete mutations keyed by config level (for JSONB _delete_key operations) */
  deleteMutations?: Partial<Record<ConfigLevel, GeneratedQuery>>;
}

/**
 * Error thrown when query generation fails due to invalid configuration.
 */
export class QueryGeneratorError extends Error {
  constructor(
    message: string,
    public readonly code: QueryGeneratorErrorCode,
    public readonly context?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'QueryGeneratorError';
  }
}

/**
 * Error codes for query generation failures.
 */
export type QueryGeneratorErrorCode =
  | 'EMPTY_SELECTION_SET'
  | 'INVALID_TABLE_NAME'
  | 'INVALID_FIELD_NAME'
  | 'INVALID_LEVEL'
  | 'MISSING_REQUIRED_FIELD'
  | 'UNSUPPORTED_STORAGE_TYPE';

// =============================================================================
// Validation Helpers
// =============================================================================

/** Valid GraphQL identifier pattern */
const VALID_IDENTIFIER_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/**
 * Validates that a string is a valid GraphQL identifier.
 */
function isValidIdentifier(name: string): boolean {
  return VALID_IDENTIFIER_PATTERN.test(name);
}

/**
 * Validates a table or field name.
 * @throws {QueryGeneratorError} If the name is invalid
 */
function validateIdentifier(name: string, type: 'table' | 'field'): void {
  if (!name || typeof name !== 'string') {
    throw new QueryGeneratorError(
      `Invalid ${type} name: ${type} name must be a non-empty string`,
      type === 'table' ? 'INVALID_TABLE_NAME' : 'INVALID_FIELD_NAME',
      { name }
    );
  }
  if (!isValidIdentifier(name)) {
    throw new QueryGeneratorError(
      `Invalid ${type} name "${name}": must start with a letter or underscore and contain only alphanumeric characters and underscores`,
      type === 'table' ? 'INVALID_TABLE_NAME' : 'INVALID_FIELD_NAME',
      { name }
    );
  }
}

/**
 * Validates that a selection set is not empty.
 * @throws {QueryGeneratorError} If the selection set is empty
 */
function validateSelectionSet(select: SelectionSet, context: string): void {
  if (!select || typeof select !== 'object' || Object.keys(select).length === 0) {
    throw new QueryGeneratorError(
      `Empty selection set for ${context}: at least one field must be selected`,
      'EMPTY_SELECTION_SET',
      { context }
    );
  }
}

// =============================================================================
// GQL String Builders
// =============================================================================

/**
 * Converts a SelectionSet to a GraphQL selection string.
 *
 * @example
 * // Input: { id: true, name: true, customer: { id: true, name: true } }
 * // Output: "id\nname\ncustomer {\n  id\n  name\n}"
 */
function buildSelectionSetString(select: SelectionSet, indent: string = '    '): string {
  const lines: string[] = [];

  for (const [field, value] of Object.entries(select)) {
    validateIdentifier(field, 'field');

    if (value === true) {
      lines.push(`${indent}${field}`);
    } else if (typeof value === 'object' && value !== null) {
      // Nested selection (relationship)
      lines.push(`${indent}${field} {`);
      lines.push(buildSelectionSetString(value as SelectionSet, indent + '  '));
      lines.push(`${indent}}`);
    }
  }

  return lines.join('\n');
}

/**
 * Converts a WhereClause to a GraphQL where argument string.
 *
 * @example
 * // Input: { active: { _eq: true }, type: { _eq: 'rideshare_selection' } }
 * // Output: '{ active: { _eq: true }, type: { _eq: "rideshare_selection" } }'
 */
function buildWhereClauseString(where: WhereClause): string {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(where)) {
    if (key === '_and' || key === '_or') {
      const clauses = value as WhereClause[];
      const clauseStrings = clauses.map(c => buildWhereClauseString(c));
      parts.push(`${key}: [${clauseStrings.join(', ')}]`);
    } else if (key === '_not') {
      parts.push(`_not: ${buildWhereClauseString(value as WhereClause)}`);
    } else {
      // Regular field comparison
      validateIdentifier(key, 'field');
      const operatorObj = value as WhereOperator;
      const operators: string[] = [];

      for (const [op, opValue] of Object.entries(operatorObj)) {
        operators.push(`${op}: ${formatGraphQLValue(opValue)}`);
      }

      parts.push(`${key}: { ${operators.join(', ')} }`);
    }
  }

  return `{ ${parts.join(', ')} }`;
}

/**
 * Converts an OrderByClause to a GraphQL order_by argument string.
 *
 * @example
 * // Input: { name: 'asc' }
 * // Output: '{ name: asc }'
 *
 * // Input: [{ name: 'asc' }, { created_at: 'desc' }]
 * // Output: '[{ name: asc }, { created_at: desc }]'
 */
function buildOrderByClauseString(orderBy: OrderByClause): string {
  if (Array.isArray(orderBy)) {
    const items = orderBy.map(item => buildSingleOrderBy(item));
    return `[${items.join(', ')}]`;
  }
  return buildSingleOrderBy(orderBy);
}

function buildSingleOrderBy(
  item: Record<string, 'asc' | 'desc' | 'asc_nulls_first' | 'asc_nulls_last' | 'desc_nulls_first' | 'desc_nulls_last'>
): string {
  const parts: string[] = [];
  for (const [field, direction] of Object.entries(item)) {
    validateIdentifier(field, 'field');
    parts.push(`${field}: ${direction}`);
  }
  return `{ ${parts.join(', ')} }`;
}

/**
 * Formats a value for GraphQL query syntax.
 */
function formatGraphQLValue(value: unknown): string {
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'string') {
    // Escape quotes and backslashes in strings
    const escaped = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `"${escaped}"`;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(formatGraphQLValue).join(', ')}]`;
  }
  if (typeof value === 'object') {
    const parts = Object.entries(value).map(([k, v]) => `${k}: ${formatGraphQLValue(v)}`);
    return `{ ${parts.join(', ')} }`;
  }
  return String(value);
}

/**
 * Converts a string to PascalCase for operation names.
 */
function toPascalCase(str: string): string {
  return str
    .split(/[-_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

/**
 * Converts a config level to its corresponding GraphQL variable type.
 */
function getLevelIdType(level: ConfigLevel): string {
  // workflowset uses Int, all others use bigint
  if (level === 'workflowset') {
    return 'Int!';
  }
  return 'bigint!';
}

/**
 * Gets the variable name for a level (e.g., 'region' -> 'regionId').
 */
function getLevelVariableName(level: ConfigLevel): string {
  return `${level}Id`;
}

/**
 * Generates a variable prefix from definition key and level.
 * Uses camelCase to create clean variable names.
 * @example 'api_enabled' + 'customer' => 'apiEnabled_customer'
 */
function generateVariablePrefix(definitionKey: string, level: ConfigLevel): string {
  const camelKey = definitionKey
    .split('_')
    .map((word, i) => (i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
  return `${camelKey}_${level}`;
}

/**
 * Applies a prefix to a variable name.
 */
function prefixVariable(varName: string, prefix: string): string {
  return `${prefix}_${varName}`;
}

// =============================================================================
// Enum Source Query Generation
// =============================================================================

/**
 * Generates a GraphQL query for fetching enum options from a database table.
 *
 * @param enumConfig - The enum source configuration
 * @returns A GeneratedQuery object with the query string and metadata
 * @throws {QueryGeneratorError} If the configuration is invalid
 *
 * @example
 * ```typescript
 * const query = generateEnumOptionsQuery({
 *   from: 'certifications',
 *   select: { id: true, name: true, customer: { id: true, name: true } },
 *   where: { active: { _eq: true } },
 *   orderBy: { name: 'asc' },
 *   mapping: { value: 'id', label: 'name' }
 * })
 * // Returns query for:
 * // query GetCertificationOptions {
 * //   certifications(where: { active: { _eq: true } }, order_by: { name: asc }) {
 * //     id
 * //     name
 * //     customer {
 * //       id
 * //       name
 * //     }
 * //   }
 * // }
 * ```
 */
export function generateEnumOptionsQuery(enumConfig: EnumSourceConfig): GeneratedQuery {
  const { from, select, where, orderBy } = enumConfig;

  validateIdentifier(from, 'table');
  validateSelectionSet(select, `enum source "${from}"`);

  const operationName = `Get${toPascalCase(from)}Options`;
  const args: string[] = [];

  if (where && Object.keys(where).length > 0) {
    args.push(`where: ${buildWhereClauseString(where)}`);
  }

  if (orderBy) {
    args.push(`order_by: ${buildOrderByClauseString(orderBy)}`);
  }

  const argsString = args.length > 0 ? `(${args.join(', ')})` : '';
  const selectionSet = buildSelectionSetString(select);

  const query = `query ${operationName} {
  ${from}${argsString} {
${selectionSet}
  }
}`;

  return {
    query,
    operationName,
    variableTypes: {},
  };
}

// =============================================================================
// Link Table Query Generation
// =============================================================================

/**
 * Generates a GraphQL query for fetching link table values.
 *
 * @param config - The link table storage configuration
 * @param level - The config level (used for variable naming)
 * @returns A GeneratedQuery object with the query string and metadata
 * @throws {QueryGeneratorError} If the configuration is invalid
 *
 * @example
 * ```typescript
 * const query = generateLinkTableGetterQuery({
 *   type: 'link_table',
 *   table: 'regionstocertifications',
 *   foreignKey: 'region_id',
 *   valueKey: 'certification_id'
 * }, 'region')
 * // Returns query for:
 * // query GetRegionstocertifications($regionId: bigint!) {
 * //   regionstocertifications(where: { region_id: { _eq: $regionId } }) {
 * //     certification_id
 * //   }
 * // }
 * ```
 */
export function generateLinkTableGetterQuery(config: LinkTableLevelStorageConfig, level: ConfigLevel): GeneratedQuery {
  const { table, foreignKey, valueKey } = config;

  validateIdentifier(table, 'table');
  validateIdentifier(foreignKey, 'field');
  validateIdentifier(valueKey, 'field');

  const operationName = `Get${toPascalCase(table)}`;
  const variableName = getLevelVariableName(level);
  const variableType = getLevelIdType(level);

  const query = `query ${operationName}($${variableName}: ${variableType}) {
  ${table}(where: { ${foreignKey}: { _eq: $${variableName} } }) {
    ${valueKey}
  }
}`;

  return {
    query,
    operationName,
    variableTypes: { [variableName]: variableType },
  };
}

/**
 * Internal: Generates a link table setter mutation with optional batching support.
 */
function generateLinkTableSetterMutationInternal(
  config: LinkTableLevelStorageConfig,
  level: ConfigLevel,
  definitionKey?: string,
  forBatching?: boolean
): GeneratedQuery {
  const { table, foreignKey } = config;

  validateIdentifier(table, 'table');
  validateIdentifier(foreignKey, 'field');

  const baseIdVar = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const insertInputType = `[${table}_insert_input!]!`;

  // Generate prefix for batching
  const prefix = forBatching && definitionKey ? generateVariablePrefix(definitionKey, level) : undefined;
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar;
  const objectsVar = prefix ? prefixVariable('objects', prefix) : 'objects';

  const deleteFieldName = `delete_${table}`;
  const deleteFieldBody = `(where: { ${foreignKey}: { _eq: $${idVar} } }) {
    affected_rows
  }`;

  const insertFieldName = `insert_${table}`;
  const insertFieldBody = `(objects: $${objectsVar}) {
    affected_rows
  }`;

  const operationName = `Set${toPascalCase(table)}`;
  const query = `mutation ${operationName}($${idVar}: ${variableType}, $${objectsVar}: ${insertInputType}) {
  ${deleteFieldName}${deleteFieldBody}
  ${insertFieldName}${insertFieldBody}
}`;

  const result: GeneratedQuery = {
    query,
    operationName,
    variableTypes: {
      [idVar]: variableType,
      [objectsVar]: insertInputType,
    },
  };

  if (forBatching && prefix) {
    result.variablePrefix = prefix;
    result.mutationFields = [
      {
        fieldName: deleteFieldName,
        fieldBody: deleteFieldBody,
        variables: [idVar],
      },
      {
        fieldName: insertFieldName,
        fieldBody: insertFieldBody,
        variables: [objectsVar],
      },
    ];
  }

  return result;
}

/**
 * Generates a GraphQL mutation for setting link table values.
 * Uses an atomic delete-then-insert pattern to replace all values.
 *
 * @param config - The link table storage configuration
 * @param level - The config level (used for variable naming)
 * @returns A GeneratedQuery object with the mutation string and metadata
 * @throws {QueryGeneratorError} If the configuration is invalid
 *
 * @example
 * ```typescript
 * const mutation = generateLinkTableSetterMutation({
 *   type: 'link_table',
 *   table: 'regionstocertifications',
 *   foreignKey: 'region_id',
 *   valueKey: 'certification_id'
 * }, 'region')
 * // Returns mutation for:
 * // mutation SetRegionstocertifications($regionId: bigint!, $objects: [regionstocertifications_insert_input!]!) {
 * //   delete_regionstocertifications(where: { region_id: { _eq: $regionId } }) {
 * //     affected_rows
 * //   }
 * //   insert_regionstocertifications(objects: $objects) {
 * //     affected_rows
 * //   }
 * // }
 * ```
 */
export function generateLinkTableSetterMutation(
  config: LinkTableLevelStorageConfig,
  level: ConfigLevel
): GeneratedQuery {
  return generateLinkTableSetterMutationInternal(config, level, undefined, false);
}

// =============================================================================
// JSONB Query Generation
// =============================================================================

/**
 * Generates a GraphQL query for fetching a config value from JSONB storage.
 *
 * @param definitionKey - The config definition key (used as the JSONB path)
 * @param level - The config level to fetch from
 * @returns A GeneratedQuery object with the query string and metadata
 * @throws {QueryGeneratorError} If the level is invalid
 *
 * @example
 * ```typescript
 * const query = generateJsonbGetterQuery('rideshare_selection_algorithm_key', 'customer')
 * // Returns query for:
 * // query GetCustomerConfig($customerId: bigint!) {
 * //   customers_by_pk(id: $customerId) {
 * //     config
 * //   }
 * // }
 * ```
 */
export function generateJsonbGetterQuery(definitionKey: string, level: ConfigLevel): GeneratedQuery {
  const levelInfo = getConfigLevelInfo(level);
  if (!levelInfo || !levelInfo.table) {
    throw new QueryGeneratorError(
      `Cannot generate JSONB getter for level "${level}": level has no associated table`,
      'INVALID_LEVEL',
      { level }
    );
  }

  const { tableName } = levelInfo.table;
  const operationName = `Storage${toPascalCase(level)}ConfigGet`;
  const variableName = getLevelVariableName(level);
  const variableType = getLevelIdType(level);

  const query = `query ${operationName}($${variableName}: ${variableType}) {
  ${tableName}_by_pk(id: $${variableName}) {
    config
  }
}`;

  return {
    query,
    operationName,
    variableTypes: { [variableName]: variableType },
  };
}

/**
 * Internal: Generates a JSONB setter mutation with optional batching support.
 */
function generateJsonbSetterMutationInternal(
  definitionKey: string,
  level: ConfigLevel,
  path?: string,
  forBatching?: boolean
): GeneratedQuery {
  const levelInfo = getConfigLevelInfo(level)
  if (!levelInfo || !levelInfo.table) {
    throw new QueryGeneratorError(
      `Cannot generate JSONB setter for level "${level}": level has no associated table`,
      'INVALID_LEVEL',
      { level }
    )
  }

  const { tableName } = levelInfo.table
  const baseIdVar = getLevelVariableName(level)
  const variableType = getLevelIdType(level)

  // Generate prefix for batching
  const prefix = forBatching ? generateVariablePrefix(definitionKey, level) : undefined
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar
  const configVar = prefix ? prefixVariable('config', prefix) : 'config'

  const fieldName = `update_${tableName}_by_pk`
  // Note: We use both _set and _append to handle null config columns
  // First _set with empty object if null, then _append the new values
  // This is necessary because _append on null JSONB is a no-op in PostgreSQL/Hasura
  const prepareFieldAlias = `prepare_${tableName}`
  const prepareFieldName = `update_${tableName}`
  const prepareFieldBody = `(where: { id: { _eq: $${idVar} }, config: { _is_null: true } }, _set: { config: {} }) {
    affected_rows
  }`
  const fieldBody = `(pk_columns: { id: $${idVar} }, _append: { config: $${configVar} }) {
    id
    config
  }`

  const operationName = `Storage${toPascalCase(level)}ConfigSet`
  const query = `mutation ${operationName}($${idVar}: ${variableType}, $${configVar}: jsonb!) {
  ${prepareFieldAlias}: ${prepareFieldName}${prepareFieldBody}
  ${fieldName}${fieldBody}
}`

  const result: GeneratedQuery = {
    query,
    operationName,
    variableTypes: {
      [idVar]: variableType,
      [configVar]: 'jsonb!',
    },
  }

  if (forBatching && prefix) {
    result.variablePrefix = prefix
    result.mutationFields = [
      {
        fieldName: prepareFieldName,
        fieldBody: prepareFieldBody,
        variables: [idVar],
      },
      {
        fieldName,
        fieldBody,
        variables: [idVar, configVar],
      },
    ]
  }

  return result
}

/**
 * Generates a GraphQL mutation for updating a config value in JSONB storage.
 *
 * @param definitionKey - The config definition key (used as the JSONB path)
 * @param level - The config level to update
 * @param path - Optional custom path within the JSONB column (defaults to definitionKey)
 * @returns A GeneratedQuery object with the mutation string and metadata
 * @throws {QueryGeneratorError} If the level is invalid
 *
 * @example
 * ```typescript
 * const mutation = generateJsonbSetterMutation('rideshare_selection_algorithm_key', 'customer')
 * // Returns mutation for:
 * // mutation SetCustomerConfig($customerId: bigint!, $config: jsonb!) {
 * //   update_customers_by_pk(pk_columns: { id: $customerId }, _append: { config: $config }) {
 * //     id
 * //     config
 * //   }
 * // }
 * ```
 */
export function generateJsonbSetterMutation(
  definitionKey: string,
  level: ConfigLevel,
  path?: string
): GeneratedQuery {
  return generateJsonbSetterMutationInternal(definitionKey, level, path, false)
}

/**
 * Generates a GraphQL mutation field for deleting a config key from JSONB storage.
 * Used for "restore" operations that remove an override to enable inheritance.
 *
 * @param definitionKey - The config key to delete
 * @param level - The config level
 * @param forBatching - Whether to generate with batching support (default: false)
 * @returns A GeneratedQuery object with the mutation string and metadata
 * @throws {QueryGeneratorError} If the level is invalid
 *
 * @example
 * ```typescript
 * const mutation = generateJsonbDeleteMutation('api_enabled', 'customer')
 * // Returns mutation for:
 * // mutation DeleteCustomerConfigKey($customerId: bigint!, $deleteKey: String!) {
 * //   update_customers_by_pk(pk_columns: { id: $customerId }, _delete_key: { config: $deleteKey }) {
 * //     id
 * //   }
 * // }
 * ```
 */
export function generateJsonbDeleteMutation(
  definitionKey: string,
  level: ConfigLevel,
  forBatching?: boolean
): GeneratedQuery {
  const levelInfo = getConfigLevelInfo(level)
  if (!levelInfo || !levelInfo.table) {
    throw new QueryGeneratorError(
      `Level "${level}" does not have a table configured`,
      'INVALID_LEVEL',
      { level, definitionKey }
    )
  }

  const { tableName } = levelInfo.table
  const baseIdVar = getLevelVariableName(level)
  const variableType = getLevelIdType(level)

  // Generate prefix for batching
  const prefix = forBatching ? generateVariablePrefix(definitionKey, level) : undefined
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar
  const keyVar = prefix ? prefixVariable('deleteKey', prefix) : 'deleteKey'

  const fieldName = `update_${tableName}_by_pk`
  const fieldBody = `(pk_columns: { id: $${idVar} }, _delete_key: { config: $${keyVar} }) {
    id
  }`

  const operationName = `Delete${toPascalCase(level)}ConfigKey`
  const query = `mutation ${operationName}($${idVar}: ${variableType}, $${keyVar}: String!) {
  ${fieldName}${fieldBody}
}`

  const result: GeneratedQuery = {
    query,
    operationName,
    variableTypes: {
      [idVar]: variableType,
      [keyVar]: 'String!',
    },
  }

  if (forBatching && prefix) {
    result.variablePrefix = prefix
    result.mutationFields = [
      {
        fieldName,
        fieldBody,
        variables: [idVar, keyVar],
      },
    ]
  }

  return result
}

// =============================================================================
// Dedicated Column Query Generation
// =============================================================================

/**
 * Generates a GraphQL query for fetching a value from a dedicated column.
 *
 * @param config - The dedicated column storage configuration
 * @param level - The config level to fetch from
 * @returns A GeneratedQuery object with the query string and metadata
 * @throws {QueryGeneratorError} If the configuration is invalid
 */
export function generateDedicatedColumnGetterQuery(
  config: DedicatedColumnLevelStorageConfig,
  level: ConfigLevel
): GeneratedQuery {
  const { table, column } = config

  validateIdentifier(table, 'table')
  validateIdentifier(column, 'field')

  const operationName = `Get${toPascalCase(table)}${toPascalCase(column)}`
  const variableName = getLevelVariableName(level)
  const variableType = getLevelIdType(level)

  const query = `query ${operationName}($${variableName}: ${variableType}) {
  ${table}_by_pk(id: $${variableName}) {
    ${column}
  }
}`

  return {
    query,
    operationName,
    variableTypes: { [variableName]: variableType },
  }
}

/**
 * Internal: Generates a dedicated column setter mutation with optional batching support.
 */
function generateDedicatedColumnSetterMutationInternal(
  config: DedicatedColumnLevelStorageConfig,
  level: ConfigLevel,
  definitionKey?: string,
  forBatching?: boolean
): GeneratedQuery {
  const { table, column, valueType, auditColumns } = config

  validateIdentifier(table, 'table')
  validateIdentifier(column, 'field')

  const baseIdVar = getLevelVariableName(level)
  const variableType = getLevelIdType(level)
  // Use valueType from config, default to String for backward compatibility
  const columnValueType = valueType || 'String'

  // Generate prefix for batching
  const prefix = forBatching && definitionKey ? generateVariablePrefix(definitionKey, level) : undefined
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar
  const valueVar = prefix ? prefixVariable('value', prefix) : 'value'

  // Build _set object with value and optional audit columns
  const setFields: string[] = [`${column}: $${valueVar}`]
  const variableTypes: Record<string, string> = {
    [idVar]: variableType,
    [valueVar]: columnValueType,
  }
  const fieldVariables: string[] = [idVar, valueVar]

  // Add audit columns if defined
  if (auditColumns?.updatedByColumn) {
    const updatedByVar = prefix ? prefixVariable('updatedBy', prefix) : 'updatedBy'
    setFields.push(`${auditColumns.updatedByColumn}: $${updatedByVar}`)
    variableTypes[updatedByVar] = 'String!'
    fieldVariables.push(updatedByVar)
  }
  if (auditColumns?.updatedAtColumn) {
    const updatedAtVar = prefix ? prefixVariable('updatedAt', prefix) : 'updatedAt'
    setFields.push(`${auditColumns.updatedAtColumn}: $${updatedAtVar}`)
    variableTypes[updatedAtVar] = 'timestamptz!'
    fieldVariables.push(updatedAtVar)
  }

  const fieldName = `update_${table}_by_pk`
  const fieldBody = `(pk_columns: { id: $${idVar} }, _set: { ${setFields.join(', ')} }) {
    id
    ${column}
  }`

  const operationName = `Set${toPascalCase(table)}${toPascalCase(column)}`
  const varDefsStr = Object.entries(variableTypes)
    .map(([name, type]) => `$${name}: ${type}`)
    .join(', ')
  const query = `mutation ${operationName}(${varDefsStr}) {
  ${fieldName}${fieldBody}
}`

  const result: GeneratedQuery = {
    query,
    operationName,
    variableTypes,
  }

  if (forBatching && prefix) {
    result.variablePrefix = prefix
    result.mutationFields = [
      {
        fieldName,
        fieldBody,
        variables: fieldVariables,
      },
    ]
  }

  return result
}

/**
 * Generates a GraphQL mutation for updating a dedicated column value.
 *
 * @param config - The dedicated column storage configuration
 * @param level - The config level to update
 * @returns A GeneratedQuery object with the mutation string and metadata
 * @throws {QueryGeneratorError} If the configuration is invalid
 */
export function generateDedicatedColumnSetterMutation(
  config: DedicatedColumnLevelStorageConfig,
  level: ConfigLevel
): GeneratedQuery {
  return generateDedicatedColumnSetterMutationInternal(config, level, undefined, false)
}

// =============================================================================
// Main Generator Function
// =============================================================================

/**
 * Generates all storage queries for a definition's storage config.
 *
 * This is the main entry point for UI code to get all necessary queries
 * for a given configuration definition.
 *
 * @param definition - The config definition containing storage config
 * @param options - Optional generator options (e.g., forBatching)
 * @returns An object containing enum options query, getter queries, and setter mutations
 * @throws {QueryGeneratorError} If the storage config is invalid
 *
 * @example
 * ```typescript
 * import { definitions } from '@hopdrive/sdk-configs'
 *
 * const queries = generateStorageQueries(definitions.minimumCertificationIds.definition)
 *
 * // queries.enumOptionsQuery - Query to fetch certification options
 * // queries.getterQueries.region - Query to get region's certifications
 * // queries.setterMutations.region - Mutation to set region's certifications
 *
 * // For batching support:
 * const batchQueries = generateStorageQueries(definition, { forBatching: true })
 * // batchQueries.setterMutations.customer?.mutationFields - Fields for combining
 * // batchQueries.setterMutations.customer?.variablePrefix - Unique prefix for variables
 * ```
 */
export function generateStorageQueries(
  definition: Definition,
  options?: GeneratorOptions
): GeneratedStorageQueries {
  const result: GeneratedStorageQueries = {
    getterQueries: {},
    setterMutations: {},
    deleteMutations: {},
  }

  const storage = definition.storage
  const forBatching = options?.forBatching ?? false

  // If no storage config, default to JSONB for all writable levels
  if (!storage) {
    for (const level of definition.writableAtLevels) {
      const levelInfo = getConfigLevelInfo(level)
      if (levelInfo?.table) {
        result.getterQueries[level] = generateJsonbGetterQuery(definition.key, level)
        result.setterMutations[level] = generateJsonbSetterMutationInternal(
          definition.key,
          level,
          undefined,
          forBatching
        )
        // JSONB storage gets delete mutations for restore operations
        result.deleteMutations![level] = generateJsonbDeleteMutation(
          definition.key,
          level,
          forBatching
        )
      }
    }
    return result
  }

  // Generate enum options query if defined
  if (storage.enum) {
    result.enumOptionsQuery = generateEnumOptionsQuery(storage.enum)
  }

  // Generate getter/setter for each writable level
  for (const level of definition.writableAtLevels) {
    const levelConfig = storage.levels?.[level]
    const levelInfo = getConfigLevelInfo(level)

    // Skip levels without tables (like 'hopdrive')
    if (!levelInfo?.table) {
      continue
    }

    if (!levelConfig) {
      // Default to JSONB storage
      result.getterQueries[level] = generateJsonbGetterQuery(definition.key, level)
      result.setterMutations[level] = generateJsonbSetterMutationInternal(
        definition.key,
        level,
        undefined,
        forBatching
      )
      // JSONB storage gets delete mutations for restore operations
      result.deleteMutations![level] = generateJsonbDeleteMutation(
        definition.key,
        level,
        forBatching
      )
    } else if (levelConfig.type === 'link_table') {
      result.getterQueries[level] = generateLinkTableGetterQuery(levelConfig, level)
      result.setterMutations[level] = generateLinkTableSetterMutationInternal(
        levelConfig,
        level,
        definition.key,
        forBatching
      )
    } else if (levelConfig.type === 'jsonb') {
      const path = levelConfig.path || definition.key
      result.getterQueries[level] = generateJsonbGetterQuery(definition.key, level);
      result.setterMutations[level] = generateJsonbSetterMutationInternal(
        definition.key,
        level,
        path,
        forBatching
      )
      // JSONB storage gets delete mutations for restore operations
      result.deleteMutations![level] = generateJsonbDeleteMutation(
        definition.key,
        level,
        forBatching
      )
    } else if (levelConfig.type === 'dedicated_column') {
      result.getterQueries[level] = generateDedicatedColumnGetterQuery(levelConfig, level)
      result.setterMutations[level] = generateDedicatedColumnSetterMutationInternal(
        levelConfig,
        level,
        definition.key,
        forBatching
      )
    } else {
      throw new QueryGeneratorError(
        `Unsupported storage type for level "${level}"`,
        'UNSUPPORTED_STORAGE_TYPE',
        { level, config: levelConfig }
      )
    }
  }

  return result
}

// =============================================================================
// Utility Functions for Building Variables
// =============================================================================

/**
 * Builds the objects array for a link table insert mutation.
 * Used by UI code to prepare the $objects variable.
 *
 * @param foreignKeyColumn - The FK column name (e.g., 'region_id')
 * @param foreignKeyValue - The FK value (e.g., the region's ID)
 * @param valueKeyColumn - The value column name (e.g., 'certification_id')
 * @param values - Array of values to insert
 * @param auditConfig - Optional audit column configuration for the table
 * @param auditValues - Optional audit values (updatedBy, updatedAt)
 * @returns Array of objects suitable for the insert mutation
 *
 * @example
 * ```typescript
 * // Without audit columns
 * const objects = buildLinkTableInsertObjects(
 *   'region_id',
 *   123n,
 *   'certification_id',
 *   [1n, 2n, 3n]
 * )
 * // Returns: [
 * //   { region_id: 123n, certification_id: 1n },
 * //   { region_id: 123n, certification_id: 2n },
 * //   { region_id: 123n, certification_id: 3n }
 * // ]
 *
 * // With audit columns
 * const objects = buildLinkTableInsertObjects(
 *   'customer_id',
 *   4,
 *   'certification_key',
 *   ['basic_auto', 'manual'],
 *   { createdByColumn: 'createdby', updatedByColumn: 'updatedby', createdAtColumn: 'createdat', updatedAtColumn: 'updated_at' },
 *   { updatedBy: 'user@example.com' }
 * )
 * // Returns: [
 * //   { customer_id: 4, certification_key: 'basic_auto', createdby: 'user@example.com', updatedby: 'user@example.com', createdat: '2026-01-25T...', updated_at: '2026-01-25T...' },
 * //   { customer_id: 4, certification_key: 'manual', createdby: 'user@example.com', updatedby: 'user@example.com', createdat: '2026-01-25T...', updated_at: '2026-01-25T...' }
 * // ]
 * ```
 */
export function buildLinkTableInsertObjects<T>(
  foreignKeyColumn: string,
  foreignKeyValue: unknown,
  valueKeyColumn: string,
  values: T[],
  auditConfig?: AuditColumnConfig,
  auditValues?: AuditValues
): Array<Record<string, unknown>> {
  // Resolve audit values with defaults
  const updatedBy = auditValues?.updatedBy ?? DEFAULT_UPDATED_BY
  const timestamp = formatTimestamp(auditValues?.updatedAt)

  return values.map((value) => {
    const obj: Record<string, unknown> = {
      [foreignKeyColumn]: foreignKeyValue,
      [valueKeyColumn]: value,
    }

    // Add audit columns if config is provided
    if (auditConfig) {
      if (auditConfig.createdByColumn) {
        obj[auditConfig.createdByColumn] = updatedBy
      }
      if (auditConfig.updatedByColumn) {
        obj[auditConfig.updatedByColumn] = updatedBy
      }
      if (auditConfig.createdAtColumn) {
        obj[auditConfig.createdAtColumn] = timestamp
      }
      if (auditConfig.updatedAtColumn) {
        obj[auditConfig.updatedAtColumn] = timestamp
      }
    }

    return obj
  })
}

/**
 * Extracts values from link table query results.
 * Used by UI code to process the getter query response.
 *
 * @param rows - Array of row objects from the query result
 * @param valueKey - The value column name (e.g., 'certification_id')
 * @returns Array of extracted values
 *
 * @example
 * ```typescript
 * const values = extractLinkTableValues(
 *   [{ certification_id: 1n }, { certification_id: 2n }],
 *   'certification_id'
 * )
 * // Returns: [1n, 2n]
 * ```
 */
export function extractLinkTableValues<T = unknown>(
  rows: Array<Record<string, unknown>>,
  valueKey: string
): T[] {
  return rows.map((row) => row[valueKey] as T)
}

// =============================================================================
// Mutation Batching Utilities
// =============================================================================

/**
 * Combines multiple GeneratedQuery mutations into a single batched mutation.
 * All mutations must have been generated with forBatching: true.
 *
 * @param mutations - Array of GeneratedQuery objects with mutationFields
 * @param operationName - Name for the combined operation (default: 'BatchedMutation')
 * @returns A BatchedMutation ready to execute
 *
 * @example
 * ```typescript
 * const queries1 = generateStorageQueries(definitions.apiEnabled.definition, { forBatching: true })
 * const queries2 = generateStorageQueries(definitions.minimumCertificationIds.definition, { forBatching: true })
 *
 * const batch = combineMutations([
 *   queries1.setterMutations.customer!,
 *   queries2.setterMutations.customer!,
 * ])
 *
 * // Execute with Hasura - all operations in single transaction
 * await hasuraClient.mutate({
 *   query: batch.query,
 *   variables: {
 *     apiEnabled_customer_customerId: 123n,
 *     apiEnabled_customer_config: { api_enabled: true },
 *     minimumCertificationIds_customer_customerId: 123n,
 *     minimumCertificationIds_customer_objects: [...],
 *   },
 * })
 * ```
 */
export function combineMutations(
  mutations: GeneratedQuery[],
  operationName: string = 'BatchedMutation'
): BatchedMutation {
  if (mutations.length === 0) {
    return {
      query: `mutation ${operationName} { __typename }`,
      operationName,
      variableTypes: {},
      variablePrefixes: {},
    }
  }

  // Verify all mutations have batching fields
  for (const mut of mutations) {
    if (!mut.mutationFields || !mut.variablePrefix) {
      throw new QueryGeneratorError(
        'All mutations must be generated with forBatching: true',
        'MISSING_REQUIRED_FIELD',
        { operationName: mut.operationName }
      )
    }
  }

  // Merge all variable types (should be unique due to prefixes)
  const mergedVariableTypes: Record<string, string> = {}
  const variablePrefixes: Record<string, string> = {}

  for (const mut of mutations) {
    Object.assign(mergedVariableTypes, mut.variableTypes)
    if (mut.variablePrefix) {
      variablePrefixes[mut.operationName] = mut.variablePrefix
    }
  }

  // Build variable definitions string
  const varDefs = Object.entries(mergedVariableTypes)
    .map(([name, type]) => `$${name}: ${type}`)
    .join(', ')

  // Build aliased fields
  const fields: string[] = []
  const aliasCounter = new Map<string, number>()

  for (const mut of mutations) {
    for (const field of mut.mutationFields!) {
      // Use prefix as alias to ensure uniqueness
      // Extract a clean suffix from field name (e.g., 'update_customers_by_pk' -> 'customers_by_pk')
      const cleanFieldSuffix = field.fieldName.replace(/^(delete_|insert_|update_)/, '')
      let alias = `${mut.variablePrefix}_${cleanFieldSuffix}`

      // Handle potential conflicts by adding a numeric suffix
      const baseAlias = alias
      const count = aliasCounter.get(baseAlias) || 0
      if (count > 0) {
        alias = `${baseAlias}_${count}`
      }
      aliasCounter.set(baseAlias, count + 1)

      fields.push(`  ${alias}: ${field.fieldName}${field.fieldBody}`)
    }
  }

  const query = `mutation ${operationName}(${varDefs}) {\n${fields.join('\n')}\n}`

  return {
    query,
    operationName,
    variableTypes: mergedVariableTypes,
    variablePrefixes,
  }
}
